import apiClient from '../../../services/apiClient';

export interface Doctor {
  id: number;
  first_name: string;
  last_name: string;
  alias?: string;
  username: string;
  email?: string;
  phone?: string;
  mobile?: string;
  whatsapp?: string;
  extension?: string;
  date_of_birth?: string;
  gender?: string;
  education?: string;
  designation?: string;
  address?: string;
  city?: string;
  country?: string;
  license_number?: string;
  professional_rnc?: string | null;
  bills_independently: boolean;
  consultation_fee?: number;
  notes?: string;
  is_active: boolean;
  is_available_for_appointments: boolean;
  specialty_id?: number;
  specialty?: { id: number; name: string };
  user_id?: number | null;
  credentials?: { username: string; temporary_password: string; email_sent: boolean };
}

interface CreateAccessResponse {
  linked: boolean;
  user_id?: number;
  user_email: string;
  temp_password?: string;
  message: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  per_page: number;
  current_page: number;
  last_page?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  meta?: Record<string, any>;
}

export const doctorService = {
  async getAll(params?: { page?: number; per_page?: number; search?: string; is_active?: boolean }): Promise<PaginatedResponse<Doctor>> {
    const queryParams = new URLSearchParams();
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

    const response = await apiClient.get<PaginatedResponse<Doctor>>(`/doctors?${queryParams.toString()}`);
    return response.data;
  },

  async search(term: string): Promise<Doctor[]> {
    const response = await apiClient.get<ApiResponse<Doctor[]>>(`/doctors/search?term=${encodeURIComponent(term)}`);
    return response.data.data || [];
  },

  async getById(id: number): Promise<Doctor> {
    const response = await apiClient.get<ApiResponse<Doctor>>(`/doctors/${id}`);
    return response.data.data;
  },

  async getWithSchedule(id: number): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(`/doctors/${id}/schedule`);
    return response.data.data;
  },

  async create(doctor: any): Promise<Doctor> {
    const response = await apiClient.post<ApiResponse<Doctor> & { meta?: { credentials?: any } }>('/doctors', doctor);
    const result = response.data.data as Doctor;
    if (response.data.meta?.credentials) {
      result.credentials = response.data.meta.credentials;
    }
    return result;
  },

  async update(id: number, doctor: any): Promise<Doctor> {
    const response = await apiClient.put<ApiResponse<Doctor>>(`/doctors/${id}`, doctor);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/doctors/${id}`);
  },

  async toggleStatus(id: number): Promise<Doctor> {
    const response = await apiClient.put<ApiResponse<Doctor>>(`/doctors/${id}/toggle-status`, {});
    return response.data.data;
  },

  async createAccess(id: number): Promise<CreateAccessResponse> {
    const response = await apiClient.post<ApiResponse<CreateAccessResponse>>(`/doctors/${id}/create-access`);
    return response.data.data;
  },
};
